/*
 * window.JJCartExtras - shared extra-cart-line registry
 *
 * WHY THIS EXISTS
 * ---------------
 * A Movie Night package page can now need MORE than one extra cart line in a
 * single Add to Event:
 *
 *   - a paid Additional Inflatable Couch line  (jj-couch-stepper)
 *   - a paid Second Concession Machine line    (jj-concession-picker)
 *
 * assets/product-form.js posts a single FormData with a single id, so any
 * second line has to come from a multi-item /cart/add.js call. Exactly one
 * element on the page may intercept the submit and make that call - two
 * interceptors would either fight over stopImmediatePropagation() or produce
 * two separate, non-atomic adds.
 *
 * <jj-couch-stepper> already owned that interception, so it stays the single
 * submitter. This registry is how every OTHER element contributes lines to
 * the call it makes, instead of building a second competing cart system.
 *
 * A PROVIDER
 * ----------
 * Any object with these optional methods. All are optional; a provider that
 * only supplies lines needs just items().
 *
 *   items(lineKey)  -> array of /cart/add.js item objects, or []
 *                      lineKey is the package line's unique _line_key, so a
 *                      provider can stamp _package_line_key for attribution.
 *   validate()      -> null when fine, or { message } when the customer still
 *                      has to do something. The submitter blocks the add and
 *                      the provider is responsible for showing its own error.
 *   reset()         -> called after a successful add, so a paid extra does
 *                      not silently get charged again on the next add.
 *
 * ORDER INDEPENDENCE
 * ------------------
 * Providers register whenever their element connects, which may be before or
 * after the submitter's own listener is bound. Nothing here depends on that
 * order: the submitter reads the registry at submit time, not at bind time.
 *
 * DEGRADATION
 * -----------
 * If this file fails to load, the submitter's guarded lookups see no registry,
 * behave exactly as they did before it existed, and the paid extra simply is
 * not offered. A provider that throws is logged and skipped rather than taking
 * the whole add down with it.
 *
 * Created 2026-08-29 for the $60 second concession.
 */

(function () {
  'use strict';

  // Idempotent: the file is referenced by more than one snippet, and two
  // identical script tags on one page would otherwise reset the providers.
  if (window.JJCartExtras) return;

  window.JJCartExtras = {
    providers: [],

    register: function (provider) {
      if (!provider) return;
      if (this.providers.indexOf(provider) === -1) this.providers.push(provider);
    },

    unregister: function (provider) {
      var index = this.providers.indexOf(provider);
      if (index !== -1) this.providers.splice(index, 1);
    },

    /* Every extra line currently being asked for. */
    items: function (lineKey) {
      var collected = [];

      this.providers.forEach(function (provider) {
        if (!provider || typeof provider.items !== 'function') return;

        try {
          var lines = provider.items(lineKey);
          if (lines && lines.length) collected = collected.concat(lines);
        } catch (error) {
          console.error('JJCartExtras: a provider failed to supply items and was skipped', error);
        }
      });

      return collected;
    },

    /*
     * How many extra lines are pending. Used by the submitter to decide
     * whether it needs to intercept at all, so the stock single-item path is
     * still used when nothing extra has been asked for.
     */
    count: function () {
      return this.items('__jj_probe__').length;
    },

    /* Anything the customer still has to resolve before the add can proceed. */
    validate: function () {
      var problems = [];

      this.providers.forEach(function (provider) {
        if (!provider || typeof provider.validate !== 'function') return;

        try {
          var problem = provider.validate();
          if (problem) problems.push(problem);
        } catch (error) {
          console.error('JJCartExtras: a provider failed to validate and was skipped', error);
        }
      });

      return problems;
    },

    reset: function () {
      this.providers.forEach(function (provider) {
        if (!provider || typeof provider.reset !== 'function') return;

        try {
          provider.reset();
        } catch (error) {
          console.error('JJCartExtras: a provider failed to reset and was skipped', error);
        }
      });
    }
  };
})();
